import React, { useCallback, useMemo } from 'react';
import { Trans } from '@lingui/macro';
import PanelContent from 'components/base/panel/panelcontent';
import ActionBlock from 'components/base/actionblock/actionblock';
import { SimpleObservationPoint } from 'util/backendapi/types/Model';
import Button, { ButtonPrimary } from 'components/base/button/button';
import { formatDatetimeForDisplay } from 'util/dates';
import { Field, Form, Formik, FormikErrors } from 'formik';
import { FieldError } from 'components/base/form/errornotice/errornotice';
import { FormItem } from 'components/base/form/FormItem';

export interface NewReadingGapFormValues {
  observation_point: number;
  reading_datetime: string;
  comment: string;
}

export interface NewReadingGapPanelViewProps {
  observationPoint: SimpleObservationPoint;
  readingDatetime: string;
  timeZone: string | null | undefined;
  onSubmit: (values: NewReadingGapFormValues) => void;
  onCancel: () => void;
}

export function NewReadingGapPanelView(props: NewReadingGapPanelViewProps) {
  const { observationPoint, readingDatetime, timeZone, onSubmit, onCancel } =
    props;

  const initialValues: NewReadingGapFormValues = useMemo(
    () => ({
      observation_point: observationPoint.id,
      reading_datetime: readingDatetime,
      comment: '',
    }),
    [observationPoint, readingDatetime]
  );

  const validate = useCallback((values: NewReadingGapFormValues) => {
    let errors: FormikErrors<NewReadingGapFormValues> = {};

    if (!values.comment) {
      errors.comment = (<Trans>Please enter a comment</Trans>) as any;
    }

    return errors;
  }, []);

  return (
    <PanelContent header={<Trans>Add reading gap</Trans>}>
      Add reading gap to <strong>{observationPoint.code}</strong> after the
      reading at{' '}
      <strong>{formatDatetimeForDisplay(readingDatetime, timeZone)}</strong>?
      <Formik
        initialValues={initialValues}
        validate={validate}
        onSubmit={onSubmit}
      >
        {({ isSubmitting, status }) => (
          <Form>
            {status}
            <FormItem label={<Trans>Comment</Trans>}>
              <Field component="textarea" name="comment" autoFocus />
              <FieldError name="comment" />
            </FormItem>

            <ActionBlock>
              <Button onClick={onCancel}>
                <Trans>Cancel</Trans>
              </Button>
              <ButtonPrimary
                id="new-reading-gap-form-submit"
                type="submit"
                disabled={isSubmitting}
                iconType="icon-save"
              >
                <Trans>Save reading gap</Trans>
              </ButtonPrimary>
            </ActionBlock>
          </Form>
        )}
      </Formik>
    </PanelContent>
  );
}
